import { type } from 'arktype';
import { dayKeySchema } from './day-key.model';
import { narrowError } from './schema-narrow';

export const ENTITLEMENT_SCOPE_KINDS_ALL = ['global', 'department', 'location', 'team', 'position', 'person', 'employment'] as const;

export const ENTITLEMENT_SCOPE_KINDS = ['person', 'employment'] as const;

export const MAX_ENTITLEMENT_DAYS = 366;

export const entitlementScopeKindSchema = type.enumerated(...ENTITLEMENT_SCOPE_KINDS).configure({
  description: 'Scope an annual vacation entitlement entry applies to; person covers every employment, employment one employment key.',
});

export type EntitlementScopeKind = typeof entitlementScopeKindSchema.t;

const annualDaysSchema = type('number').configure({ description: 'Annual vacation entitlement in days for a full year.', examples: [25] });

export const entitlementEntrySchema = type({
  id: 'string',
  scopeKind: entitlementScopeKindSchema,
  employmentKey: type('string | null').configure({ description: 'Employment key for employment scope; null for person scope.' }),
  annualDays: annualDaysSchema,
  validFrom: dayKeySchema.configure({ description: 'First day (inclusive) the entry applies.' }),
  validTo: dayKeySchema.or('null').configure({ description: 'Last day (inclusive) the entry applies; null means open-ended.' }),
}).configure({ description: 'Annual vacation entitlement override for a person or an employment.' });

export type EntitlementEntry = typeof entitlementEntrySchema.t;

type CreateEntitlementEntryInput = {
  scopeKind: EntitlementScopeKind;
  employmentKey?: string;
  validFrom: string;
  validTo?: string;
};

function createEntryError(input: CreateEntitlementEntryInput): string | null {
  if (input.validTo !== undefined && input.validTo < input.validFrom) return 'validTo on or after validFrom';
  if (input.scopeKind === 'employment') return input.employmentKey ? null : 'an employmentKey when scopeKind is employment';
  return input.employmentKey === undefined ? null : 'no employmentKey when scopeKind is person';
}

const ENTRY_MIN_YEAR = 1990;
const ENTRY_MAX_YEAR = 2100;

const entryDayKeySchema = dayKeySchema.narrow((date, ctx) => {
  const year = Number.parseInt(date.slice(0, 4), 10);
  return (year >= ENTRY_MIN_YEAR && year <= ENTRY_MAX_YEAR) || ctx.mustBe(`a date in ${ENTRY_MIN_YEAR}–${ENTRY_MAX_YEAR}`);
});

const entryAnnualDaysSchema = type(`0 < number <= ${MAX_ENTITLEMENT_DAYS}`).narrow(
  (value, ctx) => Math.abs(value * 100 - Math.round(value * 100)) < 1e-9 || ctx.mustBe('a number with at most two decimal places')
);

export const createEntitlementEntrySchema = type({
  scopeKind: entitlementScopeKindSchema,
  'employmentKey?': type('string > 0').configure({
    description: 'Employment key; required for employment scope, forbidden for person scope.',
  }),
  annualDays: entryAnnualDaysSchema.configure({
    description: 'Annual vacation entitlement in days, above 0 and at most 366, max two decimals.',
    examples: [25],
  }),
  validFrom: entryDayKeySchema.configure({ description: 'First day (inclusive) the entry applies.' }),
  'validTo?': entryDayKeySchema.configure({ description: 'Last day (inclusive) the entry applies; omit for open-ended.' }),
})
  .narrow(narrowError(createEntryError))
  .configure({ description: 'Create an annual vacation entitlement entry for the person or one of its employments.' });

export type CreateEntitlementEntry = typeof createEntitlementEntrySchema.t;

type UpdateEntitlementEntryInput = { annualDays?: number; validFrom?: string; validTo?: string | null };

function updateEntryError(input: UpdateEntitlementEntryInput): string | null {
  if (input.annualDays === undefined && input.validFrom === undefined && input.validTo === undefined) {
    return 'at least one of annualDays, validFrom or validTo';
  }
  return null;
}

export const updateEntitlementEntrySchema = type({
  'annualDays?': entryAnnualDaysSchema.configure({ description: 'New annual vacation entitlement in days.' }),
  'validFrom?': entryDayKeySchema.configure({ description: 'New first day (inclusive) the entry applies.' }),
  'validTo?': entryDayKeySchema.or('null').configure({ description: 'New last day (inclusive); null makes the entry open-ended.' }),
})
  .narrow(narrowError(updateEntryError))
  .configure({ description: 'Change the amount or validity window of an entitlement entry; at least one field is required.' });

export type UpdateEntitlementEntry = typeof updateEntitlementEntrySchema.t;

export const entitlementEntriesResponseSchema = type({
  tenantDefaultAnnualDays: type('number').configure({ description: 'Tenant default annual vacation days used when no entry applies.' }),
  entries: entitlementEntrySchema.array(),
}).configure({ description: 'Entitlement entries of a person with the tenant default for reference.' });

export type EntitlementEntriesResponse = typeof entitlementEntriesResponseSchema.t;

export const CREATE_ENTITLEMENT_ENTRY_KEYS = ['scopeKind', 'employmentKey', 'annualDays', 'validFrom', 'validTo'] as const;

export const UPDATE_ENTITLEMENT_ENTRY_KEYS = ['annualDays', 'validFrom', 'validTo'] as const;
